import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Info, Check } from "lucide-react";

// V3 building blocks (VISIBILIS_V3 PDF): measurements follow the 1448 px desktop and 390 px mobile frames.
export const V3_PRIMARY = "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[8px] bg-v3-magenta px-5 text-[15px] font-bold text-white transition-colors hover:bg-brand-magenta-dark disabled:opacity-60 lg:w-[345px] lg:text-[15.5px]";
export const V3_SECONDARY = "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[8px] border border-v3-line bg-white px-5 text-[15px] font-bold text-v3-ink transition-colors hover:bg-v3-lilac disabled:opacity-60 lg:w-[195px] lg:text-[15.5px]";
const INPUT = "h-[52px] w-full rounded-[8px] border border-v3-line bg-white px-[14px] text-[14px] text-v3-ink outline-none transition-colors placeholder:text-v3-muted focus:border-v3-magenta read-only:bg-white aria-[invalid=true]:border-red-700 lg:text-[15px]";

// focus: move keyboard/screen-reader focus to the heading when a view replaces another one in place.
export function V3Title({ title, intro, testid = "page-title", focus }) {
  const ref = useRef(null);
  useEffect(() => { if (focus) ref.current?.focus(); }, [focus, title]);
  return (
    <div className="mb-[21px] lg:mb-[28px]">
      <h1 ref={ref} tabIndex={focus ? -1 : undefined} className="hyphens-auto break-words outline-none text-[26px] font-bold leading-[37px] text-v3-ink lg:text-[34px] lg:leading-[46px]" data-testid={testid}>{title}</h1>
      {intro && <p className="mt-[10px] text-[15px] leading-[21px] text-v3-muted lg:mt-[13px] lg:text-[17px] lg:leading-[24px]" data-testid={`${testid}-intro`}>{intro}</p>}
    </div>
  );
}

// Two columns from lg on; the aside is hidden on mobile like in the template.
export function V3Columns({ main, aside, layout = "form" }) {
  const cols = { form: "lg:grid-cols-[minmax(0,590fr)_minmax(0,515fr)] lg:gap-[27px]", app: "lg:grid-cols-[minmax(0,730fr)_minmax(0,378fr)] lg:gap-[24px]",
    settings: "lg:grid-cols-[minmax(0,720fr)_minmax(0,388fr)] lg:gap-[24px]", detail: "lg:grid-cols-[minmax(0,744fr)_minmax(0,364fr)] lg:gap-[24px]",
    feed: "lg:grid-cols-[minmax(0,746fr)_minmax(0,362fr)] lg:gap-[24px]" }[layout];
  return (
    <div className={`grid grid-cols-1 items-start ${cols}`}>
      <div className="min-w-0">{main}</div>
      {aside && <aside className="hidden min-w-0 space-y-[20px] lg:block" aria-label="Zusatzinformationen">{aside}</aside>}
    </div>
  );
}

const Hint = ({ id, hint, error }) => (<>
  {hint && <span id={`${id}-hint`} className="mt-[6px] block text-[13px] text-v3-muted">{hint}</span>}
  {error && <span id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-[6px] block text-[13px] font-bold text-red-800">{error}</span>}
</>);
const described = (id, hint, error) => [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;

export function V3Field({ id, label, hint, error, testid, multiline, className = "", ...props }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="mb-[18px]">
      <label htmlFor={id} className="mb-[12px] block text-[14px] font-bold leading-[18px] text-v3-ink lg:text-[15px] lg:leading-[20px]">{label}</label>
      <Tag id={id} data-testid={testid || id} aria-invalid={error ? "true" : undefined} aria-describedby={described(id, hint, error)}
        className={`${INPUT} ${multiline ? "h-auto min-h-[52px] resize-y py-[14px] leading-[20px]" : ""} ${className}`} {...props} />
      <Hint id={id} hint={hint} error={error} />
    </div>
  );
}

// Password input (V3). Exactly one visible control: the "Passwort anzeigen" row below the field (no second eye inside the field).
export function V3PasswordField({ id, label, value, onChange, error, hint, autoComplete = "new-password", testid, showTestid, show: shownProp, onShowChange, withShowRow = true }) {
  const [own, setOwn] = useState(false);
  const show = shownProp ?? own;
  const setShow = onShowChange || setOwn;
  return (
    <div className="mb-[18px]">
      <label htmlFor={id} className="mb-[12px] block text-[14px] font-bold leading-[18px] text-v3-ink lg:text-[15px] lg:leading-[20px]">{label}</label>
      <input id={id} data-testid={testid || id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
        placeholder="Noch nicht eingegeben" aria-invalid={error ? "true" : undefined} aria-describedby={described(id, hint, error)} className={INPUT} />
      <Hint id={id} hint={hint} error={error} />
      {withShowRow && <ShowPasswordToggle show={show} onChange={setShow} controls={id} testid={showTestid || `${id}-show`} />}
    </div>
  );
}

// One toggle button with a constant name; state is conveyed by aria-pressed (no second, competing control).
export function ShowPasswordToggle({ show, onChange, controls, testid }) {
  const Icon = show ? EyeOff : Eye;
  return (
    <button type="button" onClick={() => onChange(!show)} aria-pressed={show} aria-controls={controls} data-testid={testid}
      className={`mt-[15px] flex min-h-[24px] w-fit items-center gap-[10px] rounded-[4px] text-[14px] hover:text-v3-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-magenta ${show ? "font-bold text-v3-ink" : "text-v3-muted"}`}>
      <Icon className="h-[17px] w-[17px]" aria-hidden="true" />Passwort anzeigen
    </button>
  );
}

export function V3Check({ id, checked, onChange, children, error }) {
  return (
    <div className="mb-[12px] lg:mb-[18px]">
      <label htmlFor={id} className="flex cursor-pointer items-start gap-[13px] text-[14px] leading-[21px] text-v3-ink">
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={id} aria-invalid={error ? "true" : undefined}
          className="mt-[1px] h-[20px] w-[20px] shrink-0 cursor-pointer rounded-[4px] border border-v3-line accent-[#D40A57]" />
        <span>{children}</span>
      </label>
      {error && <p role="alert" data-testid={`${id}-error`} className="ml-[33px] mt-1 text-[13px] font-bold text-red-800">{error}</p>}
    </div>
  );
}

export function V3Actions({ children, className = "mt-[20px] lg:mt-[18px]" }) {
  return <div className={`flex flex-col gap-[11px] lg:flex-row lg:gap-[14px] ${className}`}>{children}</div>;
}

const ASIDE_TONE = { lilac: "border-v3-line bg-v3-lilac", white: "border-v3-line bg-white" };

export function AsideCard({ icon: Icon, title, text, tone = "lilac", testid, children }) {
  return (
    <div className={`rounded-[12px] border px-[20px] pb-[25px] pt-[15px] ${ASIDE_TONE[tone]}`} data-testid={testid}>
      <p className="flex items-center gap-[12px] text-[17px] font-bold leading-[24px] text-v3-ink">
        {Icon && <Icon className="h-[18px] w-[18px] shrink-0 text-v3-magenta" aria-hidden="true" />}{title}
      </p>
      {text && <p className="mt-[8px] text-[14px] leading-[20px] text-v3-muted">{text}</p>}
      {children}
    </div>
  );
}

export function PhotoAside({ icon }) {
  return (
    <div className="space-y-[17px]">
      <img src="/v3-gemeinsam-erleben.jpg" alt="Vier Menschen, eine davon im Rollstuhl, sitzen lachend an einem Cafétisch." width="1200" height="800"
        className="aspect-[3/2] w-full rounded-[12px] object-cover" data-testid="v3-photo" />
      <AsideCard icon={icon} title="Gemeinsam erleben" text="Menschen kennenlernen, Aktivitäten entdecken und selbstbestimmt teilnehmen." testid="v3-photo-card" />
    </div>
  );
}

// Status banner: "sand" with a large info icon (M32/M40), "lilac" with a small one (M30/M34/M35).
export function StatusBanner({ tone = "lilac", title, text, testid, icon: Icon = Info, role }) {
  if (tone === "notice") return (
    <div role={role} data-testid={testid} className="mb-[17px] rounded-[12px] bg-v3-sand px-[20px] pb-[17px] pt-[13px]">
      <p className="flex items-center gap-[12px] text-[16px] font-bold leading-[22px] text-v3-amber"><Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />{title}</p>
      {text && <p className="mt-[9px] text-[14px] leading-[20px] text-v3-muted">{text}</p>}
    </div>
  );
  if (tone === "sand" || tone === "lilacLarge" || tone === "mint") return (
    <div role={role} data-testid={testid} className={`mb-[21px] flex min-h-[103px] items-center gap-[26px] rounded-[12px] pb-[27px] pl-[25px] pr-[20px] pt-[22px] ${{ sand: "bg-v3-sand", lilacLarge: "bg-v3-lilac", mint: "bg-v3-mint" }[tone]}`}>
      <Icon className={`h-[38px] w-[38px] shrink-0 ${tone === "mint" ? "text-v3-green" : "text-v3-ink"}`} strokeWidth={2.2} aria-hidden="true" />
      <div><p className="text-[19px] font-bold leading-[26px] text-v3-ink">{title}</p>{text && <p className="mt-[11px] text-[12.5px] leading-[17px] text-v3-muted">{text}</p>}</div>
    </div>
  );
  return (
    <div role={role} data-testid={testid} className="mb-[17px] rounded-[12px] bg-v3-lilac px-[20px] pb-[17px] pt-[13px]">
      <p className="flex items-center gap-[12px] text-[16px] font-bold leading-[22px] text-v3-ink"><Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />{title}</p>
      {text && <p className="mt-[9px] text-[14px] leading-[20px] text-v3-muted">{text}</p>}
    </div>
  );
}

export function DetailRow({ label, value, to, onClick, testid, expanded, pressed }) {
  const body = (<>
    <span className="block text-[13px] font-bold leading-[18px] text-v3-muted lg:text-[14px] lg:leading-[19px]">{label}</span>
    <span className="mt-[6px] block break-words text-[14.5px] leading-[20px] text-v3-ink lg:text-[16px] lg:leading-[22px]" data-testid={testid && `${testid}-value`}>{value}</span>
  </>);
  const cls = `block min-h-[70px] w-full rounded-[10px] border ${pressed ? "border-v3-magenta" : "border-v3-line"} bg-white px-[16px] pb-[15px] pt-[10px] text-left lg:min-h-[73px]`;
  if (onClick) return <button type="button" onClick={onClick} aria-expanded={expanded} aria-pressed={pressed} data-testid={testid} className={`${cls} transition-colors hover:border-v3-magenta`}>{body}</button>;
  return to
    ? <Link to={to} data-testid={testid} className={`${cls} transition-colors hover:border-v3-magenta`}>{body}</Link>
    : <div data-testid={testid} className={cls}>{body}</div>;
}

export const DetailList = ({ children }) => <div className="space-y-[11px]">{children}</div>;

export function V3Switch({ checked, onChange, disabled, label, testid, describedBy }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} aria-describedby={describedBy} disabled={disabled} onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-[26px] w-[46px] shrink-0 rounded-full transition-colors disabled:cursor-not-allowed ${checked ? "bg-v3-magenta" : "bg-v3-line"}`}>
      <span aria-hidden="true" className={`absolute top-[3px] h-[20px] w-[20px] rounded-full bg-white transition-transform ${checked ? "translate-x-[23px]" : "translate-x-[3px]"} left-0`} />
    </button>
  );
}

export function SettingRow({ title, value, children, testid, note }) {
  return (
    <div data-testid={testid} className="flex min-h-[87px] items-center justify-between gap-4 rounded-[10px] border border-v3-line bg-white px-[18px] pb-[20px] pt-[13px]">
      <div className="min-w-0">
        <p className="text-[16px] font-bold leading-[22px] text-v3-ink">{title}</p>
        <p className="mt-[12px] text-[14px] leading-[20px] text-v3-muted" data-testid={testid && `${testid}-value`}>{value}</p>
        {note && <p id={testid && `${testid}-note`} className="mt-[4px] text-[12.5px] leading-[17px] text-v3-amber">{note}</p>}
      </div>
      {children}
    </div>
  );
}

export function ChoiceCard({ icon: Icon, label, selected, onToggle, disabled, testid }) {
  return (
    <button type="button" aria-pressed={selected} onClick={onToggle} disabled={disabled} data-testid={testid}
      className={`relative flex min-h-[101px] flex-col justify-between rounded-[10px] border px-[17px] pb-[17px] pt-[16px] text-left transition-colors ${selected ? "border-v3-magenta bg-v3-blush" : "border-v3-line bg-white hover:border-v3-magenta"}`}>
      {Icon && <Icon className="h-[22px] w-[22px] text-v3-magenta" aria-hidden="true" />}
      {selected && <Check className="absolute right-[17px] top-[20px] h-[17px] w-[17px] text-v3-magenta" aria-hidden="true" />}
      <span className="mt-[14px] text-[17px] font-bold leading-[22px] text-v3-ink">{label}{selected && <span className="sr-only"> (ausgewählt)</span>}</span>
    </button>
  );
}

// Dark preview card of the settings views (M04/M38/M39).
export function ReadingPreview({ large, contrast, action }) {
  return (
    <div className="rounded-[12px] bg-v3-navy px-[20px] pb-[20px] pt-[22px]" data-testid="v3-reading-preview">
      <p className={`font-bold text-white ${large ? "text-[30px] leading-[38px]" : "text-[27px] leading-[34px]"}`}>Gut lesbar.</p>
      <p className={`mt-[20px] text-white ${large ? "text-[20px] leading-[28px]" : "text-[18px] leading-[26px]"} ${contrast ? "font-bold" : ""}`}>Hoher Kontrast und eine klare Textstruktur.</p>
      {action && <div className="mt-[12px]">{action}</div>}
    </div>
  );
}
